import React, { ComponentPropsWithoutRef, ElementType } from 'react';
import classNames from 'classnames';

import {
  ICON_SIZE_MAP,
  SPINNER_SIZE_MAP,
} from 'app/design-system/constants/Button';

import { ButtonIntent, ButtonSize, ButtonType } from '../../types/Button';
import { IconName } from '../../types/Icon';
import { Icon } from '../Icon';
import { Spinner } from '../Spinner';

import styles from './Button.module.scss';

export { ButtonIntent, ButtonSize, ButtonType };

const INTENT_STYLES_MAP: Record<ButtonIntent, string> = {
  [ButtonIntent.DEFAULT]: styles.intentDefault,
  [ButtonIntent.PRIMARY]: styles.intentPrimary,
  [ButtonIntent.SUCCESS]: styles.intentSuccess,
  [ButtonIntent.WARNING]: styles.intentWarning,
  [ButtonIntent.DANGER]: styles.intentDanger,
  [ButtonIntent.CALL_TO_ACTION]: styles.intentCallToAction,
};

export type ButtonProps<T extends ElementType = 'button'> = {
  component?: T;
  intent?: ButtonIntent;
  iconLeft?: IconName;
  iconRight?: IconName;
  size?: ButtonSize;
  block?: boolean;
  active?: boolean;
  disabled?: boolean;
  loading?: boolean;
  type?: ButtonType;
} & Omit<ComponentPropsWithoutRef<T>, 'component' | 'type' | 'disabled' | 'loading'>;

export function Button<T extends ElementType = 'button'>(
  {
    component,
    intent = ButtonIntent.DEFAULT,
    iconLeft,
    iconRight,
    size = ButtonSize.DEFAULT,
    block = false,
    active = false,
    disabled = false,
    loading = false,
    type = ButtonType.BUTTON,
    className,
    children,
    ...props
  }: ButtonProps<T>,
) {
  const _className = classNames(
    styles.button,
    INTENT_STYLES_MAP[intent],
    {
      [styles.sizeSmall]: size === ButtonSize.SMALL,
      [styles.sizeLarge]: size === ButtonSize.LARGE,
      [styles.block]: block,
      [styles.disabled]: !active && disabled,
      [styles.active]: active,
      [styles.loading]: loading,
    },
    className,
  );

  const Component = component || 'button';

  return (
    <Component
      type={type}
      className={_className}
      disabled={disabled || loading}
      {...props}>
      {loading && (
        <Spinner className={styles.spinner}
                 size={SPINNER_SIZE_MAP[size]} />
      )}

      {iconLeft && (
        <Icon
        data-test="icon-left"
        className={styles.icon}
        size={ICON_SIZE_MAP[size]}
        icon={iconLeft} />
      )}

      {typeof children === 'string' ? <span>{children}</span> : children}

      {iconRight && (
        <Icon
        data-test="icon-right"
        className={styles.icon}
        icon={iconRight}
        size={ICON_SIZE_MAP[size]} />
      )}
    </Component>
  );
}
